import React from 'react';
import { Icon, Popup } from 'semantic-ui-react';
import { formatCurrency } from '../services';
import { getNormalizedPercentageOfBillableCare } from '../services';
import TotalsColumnHoverPopup from './TotalsColumnHoverPopup';

/**
 * Totals row component for CDP Reports table
 * Displays aggregated financial totals across all providers
 */
const ReportTotalsRow = ({
    totals,
    hideColumn = {},
    showAsterisk,
    filters = {},
    expandableColumn,
    expandableRowLevel2,
    indexKey,
    aggregatedWriteoffs = [],
    aggregatedAdjustments = [],
    aggregatedFinancialNotes = [],
    aggregatedPayBreakdown = [],
    cdpPayDentist = null,
    cdpPayOralSurgeon = null,
    cdpPayPediatric = null,
    orthoPayTotal = null,
}) => {
    const {
        totalProductionPay, totalNormalizedBillableCare, totalBillableNotes,
        totalPayByAccount, totalHynBillableCare, totalHygienistPayContribution,
        totalPatientVisits, totalBillableCare, totalCare, totalWriteOffs,
        totalAdjustments, grandtotalBasePay, totalFinancialNotes
    } = totals;

    const totalPercentageOfBillable = getNormalizedPercentageOfBillableCare(
        totalProductionPay,
        totalNormalizedBillableCare,
        totalBillableNotes
    );
    const totalPercBillableByAccount = getNormalizedPercentageOfBillableCare(
        totalPayByAccount,
        totalNormalizedBillableCare,
        totalBillableNotes
    );
    const isAccountMode = (filters.account?.length > 0) || (filters.excludeAccount?.length > 0);
    const displayedPercent = isAccountMode ? totalPercBillableByAccount : totalPercentageOfBillable;
    const displayedNumerator = isAccountMode ? totalPayByAccount : totalProductionPay;
    const displayedNumeratorLabel = isAccountMode ? 'Account-Filtered Total Pay' : 'Total Pay';
    const displayedDenominator = totalNormalizedBillableCare + totalBillableNotes;
    const displayedTotalPay = isAccountMode ? totalPayByAccount : totalProductionPay;
    const totalPerOfbillableCareHYG = 100 * (
        totalProductionPay / (totalNormalizedBillableCare + totalBillableNotes + (totalHynBillableCare * 0.9))
    );

    // Column configs for the totals-row hover popups ------------------------
    const woColumns = [
        { key: 'providerName',       label: 'Provider' },
        { key: 'practiceDisplayName', label: 'Practice' },
        { key: 'PatNum',             label: 'Patient#' },
        { key: 'ProcDate',           label: 'Date' },
        { key: 'CarrierName',        label: 'Carrier' },
        { key: 'Remark_3',           label: 'Proc Code' },
        { key: 'Writeoff_Code',     label: 'WO Code' },
        { key: 'price',              label: 'Amount', render: item => formatCurrency(item.price) },
        { key: 'Writeoff_Note',      label: 'Note' },
    ];
    const adjColumns = [
        { key: 'providerName',       label: 'Provider' },
        { key: 'practiceDisplayName', label: 'Practice' },
        { key: 'PatNum',             label: 'Patient#' },
        { key: 'ProcDate',           label: 'Date' },
        { key: 'ProcCode',           label: 'Proc Code' },
        { key: 'ADJ_Type',         label: 'Adj Code' },
        { key: 'price',              label: 'Amount', render: item => formatCurrency(item.price) },
    ];
    const fnColumns = [
        { key: 'category',    label: 'Category' },
        { key: 'dentist',     label: 'Provider' },
        { key: 'practice',    label: 'Practice' },
        { key: 'title',       label: 'Title' },
        { key: 'dateString',  label: 'Date' },
        { key: 'sageAccount', label: 'Account' },
        { key: 'price',       label: 'Amount', render: item => formatCurrency(item.price) },
        { key: 'bcare',       label: 'B.Care', render: item => item.bcare ? formatCurrency(Number(item.bcare)) : '-' },
    ];
    // Notes that carry billable care (bcare) — own heart + hover, separate from the
    // Financial Notes popup so hovering the heart shows the billable breakdown only.
    const billableNoteItems = aggregatedFinancialNotes
        .filter(n => parseFloat(n.bcare))
        .sort((a, b) => Math.abs(b.bcare) - Math.abs(a.bcare));
    const billableNotesSum = billableNoteItems.reduce((s, n) => s + (parseFloat(n.bcare) || 0), 0);
    // -----------------------------------------------------------------------
    // Combined pay summary: CDP roles + Ortho (NDP) — used by the totalPay popup
    const CDP_PAY_ROLES = ['Dentist', 'Oral Surgeon', 'Pediatric'];

    // Calculate fallback totals from page data (aggregatedPayBreakdown)
    const fallbackCdpTotals = { 'Dentist': 0, 'Oral Surgeon': 0, 'Pediatric': 0 };
    aggregatedPayBreakdown.forEach(item => {
        if (Object.prototype.hasOwnProperty.call(fallbackCdpTotals, item.role)) {
            fallbackCdpTotals[item.role] += item.price || 0;
        }
    });

    // Determine if backend values are all zero
    const backendDentist = (cdpPayDentist !== null && cdpPayDentist !== undefined) ? Number(cdpPayDentist) : 0;
    const backendOralSurgeon = (cdpPayOralSurgeon !== null && cdpPayOralSurgeon !== undefined) ? Number(cdpPayOralSurgeon) : 0;
    const backendPediatric = (cdpPayPediatric !== null && cdpPayPediatric !== undefined) ? Number(cdpPayPediatric) : 0;
    const backendAllZero = backendDentist === 0 && backendOralSurgeon === 0 && backendPediatric === 0;

    // Use backend values, but fallback to page data if all backend values are zero
    const cdpRoleTotals = {
        'Dentist': backendAllZero ? fallbackCdpTotals['Dentist'] : backendDentist,
        'Oral Surgeon': backendAllZero ? fallbackCdpTotals['Oral Surgeon'] : backendOralSurgeon,
        'Pediatric': backendAllZero ? fallbackCdpTotals['Pediatric'] : backendPediatric
    };

    const orthoTotal = (orthoPayTotal !== null && orthoPayTotal !== undefined) ? Number(orthoPayTotal) : null;
    const combinedGrandTotal = CDP_PAY_ROLES.reduce((s, r) => s + cdpRoleTotals[r], 0) + (orthoTotal || 0);
    // -----------------------------------------------------------------------

    return (
        <tr className="font-weight-bold" key={"row-total-level-0" + indexKey}>
            {!hideColumn.selectAll && <td />}
            {!hideColumn.lockStatus && <td />}
            {!hideColumn.provider && <td />}
            {!hideColumn.ABBR && expandableColumn && <td />}
            {!hideColumn.practice && <td className="font-weight-bold">Total</td>}
            {!hideColumn.contractType && <td />}
            {!hideColumn.workTimeType && expandableColumn && <td />}
            {!hideColumn.workedDays && <td />}
            {!hideColumn.scheduledDays && <td />}
            {!hideColumn.RTOApproved && expandableColumn && <td />}
            {!hideColumn.FTOApproved && expandableColumn && <td />}
            {!hideColumn.patientVisits && <td className="font-weight-bold"> {totalPatientVisits}</td>}
            {!hideColumn.bCPV &&
                <td>{formatCurrency(totalPatientVisits ? totalBillableCare / totalPatientVisits : 0)}</td>}
            {!hideColumn.patientVisitsPerDay && <td />}
            {!hideColumn.rampStartDate && expandableColumn && <td />}
            {!hideColumn.rampEndDate && expandableColumn && <td />}
            {!hideColumn.procedureDate && expandableRowLevel2 && <td />}
            {!hideColumn.startEnd && expandableRowLevel2 && <td />}
            {!hideColumn.care && <td> {formatCurrency(totalCare)}</td>}
            {!hideColumn.writeOffs && (
                <td>
                    <TotalsColumnHoverPopup
                        trigger={formatCurrency(totalWriteOffs * -1)}
                        items={aggregatedWriteoffs}
                        codeField="Writeoff_Code"
                        title="Write-Off codes summary (filtered):"
                        columns={woColumns}
                    />
                </td>
            )}
            {!hideColumn.adjustments && (
                <td>
                    <TotalsColumnHoverPopup
                        trigger={formatCurrency(totalAdjustments)}
                        items={aggregatedAdjustments}
                        codeField="ADJ_Type"
                        title="Adjustment codes summary (filtered):"
                        columns={adjColumns}
                    />
                </td>
            )}
            {!hideColumn.careFraction && <td />}
            {!hideColumn.collectionsGoxGozTotal && expandableColumn && <td />}
            {!hideColumn.bCare && <td>
                {formatCurrency(totalBillableCare)}<br />
                {showAsterisk && <p className="font-weight-light">*{formatCurrency(totalHynBillableCare)}*</p>}
            </td>}
            {!hideColumn.totalBasePay && <td>{formatCurrency(grandtotalBasePay)}</td>}
            {!hideColumn.financialNotes && (
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
                    {billableNoteItems.length > 0 && (
                        <Popup
                            on="hover"
                            hoverable
                            position="top center"
                            style={{ zIndex: 1050, padding: '0.75rem' }}
                            trigger={<Icon name="heartbeat" color="blue" className="mr-1 cursorPointer" />}
                        >
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>
                                Billable care from Financial Notes: {formatCurrency(billableNotesSum)}
                            </div>
                            <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                                <table className="table table-striped table-sm my-0" style={{ fontSize: '0.82em' }}>
                                    <thead>
                                        <tr>
                                            <th>Provider</th><th>Practice</th><th>Title</th><th>Date</th><th>B.Care</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {billableNoteItems.map((n, i) => (
                                            <tr key={i}>
                                                <td>{n.providerName || n.dentist || '-'}</td>
                                                <td>{n.practice || '-'}</td>
                                                <td>{n.title || n.category || '-'}</td>
                                                <td>{n.dateString || '-'}</td>
                                                <td>{formatCurrency(Number(n.bcare))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Popup>
                    )}
                    <TotalsColumnHoverPopup
                        trigger={formatCurrency(totalFinancialNotes)}
                        items={aggregatedFinancialNotes}
                        codeField="category"
                        title="Financial Notes by category (filtered):"
                        columns={fnColumns}
                    />
                  </div>
                </td>
            )}
            {!hideColumn.totalPay && (
                <td>
                    <Popup
                        on="hover"
                        hoverable
                        position="top center"
                        trigger={
                            <span style={{ cursor: 'help' }}>
                                {formatCurrency(displayedTotalPay)}
                                {showAsterisk && totalHygienistPayContribution !== 0 && (
                                    <p className="font-weight-light">*{formatCurrency(totalHygienistPayContribution)}*</p>
                                )}
                            </span>
                        }
                        content={
                            <table style={{ borderCollapse: 'collapse', minWidth: 300 }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', padding: '4px 12px', borderBottom: '1px solid rgba(34,36,38,.2)', whiteSpace: 'nowrap' }}>Report / Role</th>
                                        <th style={{ textAlign: 'right', padding: '4px 12px', borderBottom: '1px solid rgba(34,36,38,.2)' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {CDP_PAY_ROLES.map(role => (
                                        <tr key={role}>
                                            <td style={{ padding: '3px 12px', whiteSpace: 'nowrap' }}>CDP — {role}</td>
                                            <td style={{ padding: '3px 12px', textAlign: 'right' }}>{formatCurrency(cdpRoleTotals[role])}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ borderTop: '1px solid rgba(34,36,38,.12)', color: '#2185d0' }}>
                                        <td style={{ padding: '3px 12px', whiteSpace: 'nowrap' }}>Ortho (NDP) Pay</td>
                                        <td style={{ padding: '3px 12px', textAlign: 'right' }}>
                                            {orthoTotal !== null
                                                ? formatCurrency(orthoTotal)
                                                : <em style={{ color: 'rgba(0,0,0,.35)', fontSize: '.88em' }}>visit Ortho Reports first</em>
                                            }
                                        </td>
                                    </tr>
                                    <tr style={{ fontWeight: 700, borderTop: '2px solid rgba(34,36,38,.25)' }}>
                                        <td style={{ padding: '5px 12px', whiteSpace: 'nowrap' }}>Grand Total</td>
                                        <td style={{ padding: '5px 12px', textAlign: 'right' }}>{formatCurrency(combinedGrandTotal)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        }
                    />
                </td>
            )}
            {!hideColumn.normalizedPercentageOfBillableCare && <td>
                <Popup
                    on="hover"
                    position="top center"
                    trigger={<span className="cursorPointer">{displayedPercent.toFixed(2)}%</span>}
                    content={
                        <div style={{ minWidth: 360, lineHeight: 1.5 }}>
                            <div style={{ marginBottom: 6 }}><b>Formula</b>: % Billable = (Numerator / Denominator) x 100</div>
                            <div><b>Mode</b>: {isAccountMode ? 'Account Filtered' : 'Standard'}</div>
                            <div><b>Numerator</b>: {formatCurrency(displayedNumerator)}</div>
                            <div><b>Numerator Source</b>: {displayedNumeratorLabel}</div>
                            <div><b>Denominator</b>: {formatCurrency(displayedDenominator)}</div>
                            <div><b>Denominator Source</b>: Normalized bCare + Billable Notes</div>
                            <div style={{ marginLeft: 8 }}>
                                = {formatCurrency(totalNormalizedBillableCare)} + {formatCurrency(totalBillableNotes)}
                            </div>
                            <div style={{ marginLeft: 8, color: 'rgba(0,0,0,.6)' }}>
                                where Normalized bCare is summed from provider normalized values and Billable Notes is summed from provider billable notes.
                            </div>
                            <div style={{ marginTop: 6 }}><b>Expanded</b>: ({formatCurrency(displayedNumerator)} / ({formatCurrency(totalNormalizedBillableCare)} + {formatCurrency(totalBillableNotes)})) x 100</div>
                            <div style={{ marginBottom: 8 }}><b>Result</b>: {displayedPercent.toFixed(2)}%</div>

                            <div style={{ borderTop: '1px solid rgba(34,36,38,.15)', paddingTop: 8, marginTop: 4 }}>
                                <div><b>bCare in table</b>: {formatCurrency(totalBillableCare)}</div>
                                <div><b>Normalized bCare used here</b>: {formatCurrency(totalNormalizedBillableCare)}</div>
                                <div><b>Billable Notes</b>: {formatCurrency(totalBillableNotes)}</div>
                                <div style={{ marginTop: 6 }}>
                                    <b>How Normalized bCare is derived</b>: per provider,
                                    Normalized bCare = careFraction% x (Care - Write Off + Adjustment), then summed.
                                </div>
                                <div style={{ marginTop: 4, color: 'rgba(0,0,0,.6)' }}>
                                    This is why Normalized bCare may differ from the bCare column value.
                                </div>
                            </div>
                        </div>
                    }
                />
                <br />
                {showAsterisk && <p className="font-weight-light">*{totalPerOfbillableCareHYG.toFixed(2)}%*</p>}
            </td>}
        </tr>
    );
};

export default ReportTotalsRow;
